import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { formatPhoneNumber, isInternalRoute } from '@stitch/pbx-adapter/catalog';
import { lookup } from '@stitch/pbx-adapter/describe';
import { api, textingApi, type NumberOrder, type OpenInventory, type TextRoutesPage } from '../../api/client';
import { NUMBER_WORDS } from './TextingRegistration';
import { ORDER_STATUS, OrderLines } from '../../components/numbers';
import { DestinationPicker, subLine } from '../../components/pickers';
import { ReviewDrawer } from '../../components/ReviewDrawer';
import { useTenant } from '../../components/Shell';
import { Button, Card, Chip, Dialog, Empty, LoadError, Loading, Notice, PageHeader, Toast } from '../../components/ui';
import { TextsGoToDrawer, useCompanyAdmin } from '../../components/TextRouting';
import { adminsNotice, textsGoTo } from '../../lib/textRouting';
import { dateOnly, plural, shortWhen } from '../../lib/format';
import { orderingBlocked } from './GetNumbers';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { useAuth } from '../../lib/auth';

/** "+14105550100" → "(410) 555-0100" for North American numbers; others as they are. */
export const prettyNumber = formatPhoneNumber;

const PAGES: Record<string, string> = {
  autoAttendant: 'auto-attendants',
  ringGroup: 'ring-groups',
  callQueue: 'queues',
  timeRule: 'time-rules',
  callPark: 'call-park',
  person: 'people',
  mailbox: 'voicemail'
};

type Staged = { number: string; from: string; to: string };

/** What a number is assigned to, for filtering: the groups people think in. */
type Assignment = 'all' | 'person' | 'autoAttendant' | 'callQueue' | 'ringGroup' | 'voicemail' | 'other' | 'inventory' | 'none';
const ASSIGNMENTS: { value: Assignment; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'person', label: 'People' },
  { value: 'autoAttendant', label: 'Auto attendants' },
  { value: 'callQueue', label: 'Call queues' },
  { value: 'ringGroup', label: 'Ring groups' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'other', label: 'Other' },
  { value: 'inventory', label: 'Open inventory' },
  { value: 'none', label: 'Not assigned' }
];

function assignmentOf(n: { destination: string; elsewhere?: boolean }, kind: string, inventory: string | null): Exclude<Assignment, 'all'> {
  if (n.elsewhere) return 'other';
  if (!n.destination) return 'none';
  if (inventory && n.destination === inventory) return 'inventory';
  if (kind === 'person') return 'person';
  if (kind === 'mailbox' || kind === 'voicemail') return 'voicemail';
  if (kind === 'autoAttendant' || kind === 'callQueue' || kind === 'ringGroup') return kind;
  return 'other';
}

/** The company's phone numbers, where calls to each one go, and changing that. */
export function TenantNumbers() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const numbers = useData(() => api.numbers(d), [d]);
  const dir = useData(() => api.directory(d).catch(() => undefined), [d]);
  const [editing, setEditing] = useState<{ number: string; destination: string } | null>(null);
  const [choice, setChoice] = useState('');
  const [staged, setStaged] = useState<Staged | null>(null);
  const [reviewKey, setReviewKey] = useState(0);
  const [show, setShow] = useState<Assignment>('all');
  const [q, setQ] = useState('');
  // Texts go to (1.12.32, 2959): the company's own admins see where each number's texts go, and change it.
  const admin = useCompanyAdmin(d);
  const texts = useData(() => (admin ? api.textRoutes(d).catch(() => null) : Promise.resolve(null)), [d, admin]);
  // Just saved: shown until the page's own copy loads again.
  const [textRoutes, setTextRoutes] = useState<TextRoutesPage | null>(null);
  useEffect(() => setTextRoutes(null), [texts.data]);
  const routesNow = textRoutes ?? texts.data ?? null;
  const routeOf = useMemo(() => new Map((routesNow?.numbers ?? []).map((r) => [r.number, r])), [routesNow]);
  // Whether each number can text (1.13.0, 3073): registered, waiting, or not.
  const texting = useData(() => (admin ? textingApi.numbers(d).catch(() => null) : Promise.resolve(null)), [d, admin, routesNow]);
  const textStatus = useMemo(() => new Map((texting.data?.numbers ?? []).map((n) => [n.number, n])), [texting.data]);
  const [textsOf, setTextsOf] = useState<string | null>(null);
  const [adminsOnly, setAdminsOnly] = useState(false);
  const [toast, setToast] = useState('');
  const toAdmins = routesNow?.toAdmins ?? 0;
  const goesToAdmins = (n: string) => {
    const r = routeOf.get(n);
    return Boolean(r?.canText && r.reach === 'admins');
  };

  const { state } = useAuth();
  const staff = Boolean(state?.user?.staff);
  const carrier = useData(() => api.carrierStatus(d).catch(() => undefined), [d]);
  const orders = useData(() => api.numberOrders(d).then((r) => r.orders).catch(() => [] as NumberOrder[]), [d]);
  const canBuy = Boolean(carrier.data && !orderingBlocked(carrier.data, staff));
  const canMove = Boolean(carrier.data?.carrier && carrier.data.writes && carrier.data.routed);
  const everything = numbers.data?.numbers ?? [];
  // Platform trigger numbers (+1 899) are Stitch's own routing between its phone platforms: they
  // get their own section, apart from the company's real numbers, and only staff change them.
  const list = useMemo(() => everything.filter((n) => !isInternalRoute(n.number)), [everything]);
  const routes = useMemo(() => everything.filter((n) => isInternalRoute(n.number)), [everything]);
  // Open inventory (1.13.0): numbers nobody uses yet, answered with the company's name.
  const inv = numbers.data?.inventory ?? null;
  const invExt = inv?.extension ?? null;
  const assigned = useMemo(() => new Map(list.map((n) => [n.number, assignmentOf(n, lookup(dir.data, n.destination).kind, invExt)])), [list, dir.data, invExt]);
  const counts = useMemo(() => {
    const c = new Map<Assignment, number>([['all', list.length]]);
    for (const a of assigned.values()) c.set(a, (c.get(a) ?? 0) + 1);
    return c;
  }, [assigned, list.length]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase().replace(/[\s()+-]/g, '');
    return list.filter((n) => {
      if (adminsOnly && !goesToAdmins(n.number)) return false;
      if (show !== 'all' && assigned.get(n.number) !== show) return false;
      if (!needle) return true;
      const info = lookup(dir.data, n.destination);
      const goes = invExt && n.destination === invExt ? 'openinventory' : info.name;
      return [n.number, n.label ?? '', goes, n.destination].some((v) => v.toLowerCase().replace(/[\s()+-]/g, '').includes(needle));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, show, q, assigned, dir.data, adminsOnly, routeOf, invExt]);
  const canChange = Boolean(numbers.data?.canChange);
  const nowhere = list.filter((n) => !n.destination && !n.elsewhere).length;
  const waiting = invExt ? list.filter((n) => n.destination === invExt).length : 0;
  const nameOfDest = (v: string) => (!v ? 'nowhere' : v === invExt ? 'open inventory' : lookup(dir.data, v).name);
  const [putting, setPutting] = useState(false);
  const [putError, setPutError] = useState('');
  async function putBack(number: string) {
    setPutting(true);
    setPutError('');
    try {
      const r = await api.putInInventory(d, number);
      setEditing(null);
      setToast(r.moved ? `${prettyNumber(number)} is back in open inventory.` : `${prettyNumber(number)} was already in open inventory.`);
      numbers.reload();
    } catch (err) {
      setPutError(err instanceof Error ? err.message : 'That didn’t go through. Try again.');
    } finally {
      setPutting(false);
    }
  }

  function open(number: string, destination: string) {
    setEditing({ number, destination });
    setChoice(destination === invExt ? '' : destination);
    setPutError('');
  }

  return (
    <>
      <PageHeader
        eyebrow={numbers.data ? `${plural(list.length, 'number')}${nowhere ? ` · ${nowhere} not going anywhere` : ''}${waiting ? ` · ${waiting} in open inventory` : ''}${routes.length ? ` · ${plural(routes.length, 'internal route')}` : ''}` : d}
        title="Numbers"
        actions={
          <>
            <label className="search">
              <Icon name="search" size={17} strokeWidth={2} />
              <span className="sr-only">Search numbers</span>
              <input type="search" placeholder="Search a number or who it goes to" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            {canBuy && (
              <Link className="btn primary" to={`/t/${encodeURIComponent(d)}/numbers/new`}>
                <Icon name="plus" size={17} strokeWidth={2.2} /> Get numbers
              </Link>
            )}
          </>
        }
      />
      <div className="content">
        {(orders.data?.length ?? 0) > 0 && <Orders d={d} orders={orders.data!} reload={orders.reload} />}
        {numbers.data && !canChange && <Notice>Where each number goes is changed by Stitch for this company for now. Ask us and we’ll do it.</Notice>}
        {toAdmins > 0 && (
          <Notice tone="warn" title={adminsNotice(toAdmins)}>
            Nobody who uses Stitch gets {toAdmins === 1 ? 'its' : 'their'} texts: {toAdmins === 1 ? 'it goes' : 'they go'} to a menu, nowhere, someone not in Stitch, or a team that doesn’t take
            texts. Choose who gets them under Texts go to.{' '}
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                setAdminsOnly(true);
                setShow('all');
                setQ('');
                document.getElementById('numbers-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {toAdmins === 1 ? 'Show it' : 'Show them'}
            </button>
          </Notice>
        )}
        {list.length > 0 && (
          <div className="filters" role="group" aria-label="Show numbers assigned to">
            {ASSIGNMENTS.filter((a) => a.value === 'all' || (counts.get(a.value) ?? 0) > 0).map((a) => (
              <button
                key={a.value}
                type="button"
                aria-pressed={!adminsOnly && show === a.value}
                onClick={() => {
                  setShow(a.value);
                  setAdminsOnly(false);
                }}
              >
                {a.label} <span className="filter-count">{counts.get(a.value) ?? 0}</span>
              </button>
            ))}
            {(toAdmins > 0 || adminsOnly) && (
              <button type="button" aria-pressed={adminsOnly} onClick={() => setAdminsOnly(!adminsOnly)}>
                Texts to admins only <span className="filter-count">{toAdmins}</span>
              </button>
            )}
          </div>
        )}
        <Card id="numbers-list">
          {numbers.error ? (
            <LoadError error={numbers.error} retry={numbers.reload} />
          ) : !numbers.data ? (
            <Loading label="Loading numbers" />
          ) : list.length === 0 ? (
            <Empty title="No numbers yet">{canBuy ? 'Get new numbers, or move the ones you have to Stitch, with the buttons above.' : 'Numbers appear here once Stitch adds them to your phone system.'}</Empty>
          ) : rows.length === 0 ? (
            <Empty title="No numbers match">Try another filter or search.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Number</th>
                    <th scope="col">Calls go to</th>
                    {routesNow && <th scope="col">Texts go to</th>}
                    {canChange && (
                      <th scope="col">
                        <span className="sr-only">Change</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((n) => {
                    const info = lookup(dir.data, n.destination);
                    const page = PAGES[info.kind];
                    const pretty = prettyNumber(n.number);
                    const route = routeOf.get(n.number);
                    const goes = route?.canText ? textsGoTo(route, routesNow!.teams) : null;
                    return (
                      <tr key={n.number}>
                        <td>
                          <span className="cell-stack">
                            <span className="mono">{pretty}</span>
                            {n.label && <small className="muted">{n.label}</small>}
                          </span>
                        </td>
                        <td>
                          {n.elsewhere ? (
                            <span className="cell-stack">
                              <span>Somewhere outside this company</span>
                              <small className="muted">Ask Stitch about this number</small>
                            </span>
                          ) : !n.destination ? (
                            <Chip small tone="warn">
                              Nowhere yet
                            </Chip>
                          ) : n.destination === invExt ? (
                            <span className="cell-stack">
                              <Chip small tone="brand">
                                Open inventory
                              </Chip>
                              <small className="muted">Answered with your company’s name</small>
                            </span>
                          ) : (
                            <span className="cell-stack">
                              {page ? <Link to={`/t/${encodeURIComponent(d)}/${page}`}>{info.name}</Link> : <span>{info.name}</span>}
                              <small className="muted">{subLine(info)}</small>
                            </span>
                          )}
                        </td>
                        {routesNow && (
                          <td className="texts-go-cell">
                            {!route ? (
                              <span className="muted">–</span>
                            ) : !goes ? (
                              <small className="muted">Not set up for texting</small>
                            ) : (
                              <button
                                type="button"
                                className="link-btn texts-go-to"
                                aria-label={`Texts to ${pretty} go to ${goes.text === 'Admins' ? 'your admins only' : goes.text}${goes.note ? ` (${goes.note})` : ''}. Change`}
                                onClick={() => {
                                  setToast('');
                                  setTextsOf(n.number);
                                }}
                              >
                                <span className="cell-stack">
                                  {goes.tone === 'admins' ? (
                                    <Chip small tone="warn">
                                      Admins
                                    </Chip>
                                  ) : (
                                    <span className={goes.tone === 'off' ? 'texts-go-off' : undefined}>{goes.text}</span>
                                  )}
                                  {goes.note && <small>{goes.note}</small>}
                                  {textStatus.get(n.number) && (
                                    <span title={textStatus.get(n.number)!.reason ?? undefined}>
                                      <Chip small tone={NUMBER_WORDS[textStatus.get(n.number)!.state].tone}>
                                        {NUMBER_WORDS[textStatus.get(n.number)!.state].label}
                                      </Chip>
                                    </span>
                                  )}
                                </span>
                                <Icon name="chev" size={14} />
                              </button>
                            )}
                          </td>
                        )}
                        {canChange && (
                          <td className="cell-action">
                            {(() => {
                              const placed = Boolean(n.destination) && n.destination !== invExt;
                              return (
                                <Button
                                  size="small"
                                  variant={placed ? 'secondary' : 'primary'}
                                  icon={placed ? 'edit' : 'flow'}
                                  aria-label={placed ? `Change where ${pretty} goes` : `Choose where ${pretty} goes`}
                                  onClick={() => open(n.number, n.destination)}
                                >
                                  {placed ? 'Change' : 'Choose where it goes'}
                                </Button>
                              );
                            })()}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {canMove && (
            <div className="card-foot">
              <Icon name="truck" size={17} />
              <span className="muted grow">Have numbers with another phone company? Bring them with you: callers keep dialing the same numbers.</span>
              <Link className="btn small secondary" to={`/t/${encodeURIComponent(d)}/numbers/move`}>
                Move numbers to Stitch
              </Link>
            </div>
          )}
        </Card>
        {inv && <InventoryCard d={d} company={tenant.name} inv={inv} waiting={waiting} canChange={canChange} onShow={() => (setShow('inventory'), setAdminsOnly(false), setQ(''))} onSaved={numbers.reload} />}
        {routes.length > 0 && (
          <Card title={`Internal routes · ${routes.length}`}>
            <div className="card-body routes-intro">
              <p className="muted">
                Numbers starting +1 899 are how Stitch passes calls between its phone platforms. Callers can’t dial them, and they’re never anyone’s caller ID.
                {staff ? ' Customers see them here but can’t change them.' : ' Stitch support looks after them; ask us if one should go somewhere else.'}
              </p>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Route</th>
                    <th scope="col">Calls go to</th>
                    {staff && canChange && (
                      <th scope="col">
                        <span className="sr-only">Change</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {routes.map((n) => {
                    const info = lookup(dir.data, n.destination);
                    const page = PAGES[info.kind];
                    return (
                      <tr key={n.number}>
                        <td>
                          <span className="cell-stack">
                            <span>
                              {prettyNumber(n.number)} <Chip small>Internal</Chip>
                            </span>
                            <small className="muted mono">{n.number}</small>
                          </span>
                        </td>
                        <td>
                          {!n.destination ? (
                            <span className="muted">Not in use</span>
                          ) : (
                            <span className="cell-stack">
                              {page ? <Link to={`/t/${encodeURIComponent(d)}/${page}`}>{info.name}</Link> : <span>{info.name}</span>}
                              <small className="muted">{subLine(info)}</small>
                            </span>
                          )}
                        </td>
                        {staff && canChange && (
                          <td className="cell-action">
                            <Button size="small" variant="secondary" icon="edit" aria-label={`Change where ${prettyNumber(n.number)} goes`} onClick={() => open(n.number, n.destination)}>
                              Change
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {editing && (
        <Dialog
          open
          onClose={() => setEditing(null)}
          title={`Where calls to ${prettyNumber(editing.number)} go`}
          foot={
            <>
              <Button
                variant="primary"
                disabled={!choice || choice === editing.destination}
                onClick={() => {
                  setStaged({ number: editing.number, from: editing.destination, to: choice });
                  setEditing(null);
                  setReviewKey((k) => k + 1);
                }}
              >
                Review
              </Button>
              <Button variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </>
          }
        >
          <DestinationPicker
            label="Calls go to"
            value={choice}
            onChange={setChoice}
            dir={dir.data}
            mode="account"
            clearable={false}
            help="A person, voicemail box, auto attendant, ring group, call queue or time of day rule."
          />
          {invExt && editing.destination !== invExt && !isInternalRoute(editing.number) ? (
            <div className="inventory-back">
              <p className="muted small-print">Not in use any more? Put it back in open inventory: callers hear your company’s name, and it waits here until it’s needed.</p>
              <Button size="small" variant="secondary" disabled={putting} onClick={() => void putBack(editing.number)}>
                {putting ? 'Putting it back…' : 'Put back in open inventory'}
              </Button>
              {putError && <span className="field-error">{putError}</span>}
            </div>
          ) : (
            !editing.destination && <p className="muted small-print">Once it points somewhere, a number can be moved but not sent back to nowhere here.</p>
          )}
        </Dialog>
      )}

      {textsOf && routesNow && routeOf.get(textsOf) && (
        <TextsGoToDrawer
          domain={d}
          row={routeOf.get(textsOf)!}
          page={routesNow}
          name={list.find((n) => n.number === textsOf)?.label}
          onClose={() => setTextsOf(null)}
          onSaved={(next, message) => {
            setTextRoutes(next);
            setTextsOf(null);
            setToast(message);
            texts.reload();
          }}
        />
      )}
      {toast && <Toast message={toast} tone="ok" onDismiss={() => setToast('')} />}

      <ReviewDrawer
        key={reviewKey}
        open={staged !== null}
        tenant={d}
        items={
          staged
            ? [
                {
                  key: staged.number,
                  description: `Send calls to ${prettyNumber(staged.number)} to ${nameOfDest(staged.to)}${staged.from ? ` (now ${nameOfDest(staged.from)})` : ''}`,
                  intent: { op: 'account.update', kind: 'number', extension: staged.number, set: { destination: staged.to } }
                }
              ]
            : []
        }
        onUnstage={() => setStaged(null)}
        onClose={(applied) => {
          setStaged(null);
          if (applied) numbers.reload();
        }}
      />
    </>
  );
}

/**
 * Open inventory (1.13.0, Teampace 3035): what callers hear on numbers nobody uses yet, and the
 * company admins' one choice about it: whether to offer the main menu (press 1).
 */
function InventoryCard({ d, company, inv, waiting, canChange, onShow, onSaved }: { d: string; company: string; inv: OpenInventory; waiting: number; canChange: boolean; onShow: () => void; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // Shown at once; back to what's saved if the change doesn't go through.
  const [pressOne, setPressOne] = useState(inv.pressOne);
  useEffect(() => setPressOne(inv.pressOne), [inv.pressOne]);
  const set = async (on: boolean) => {
    setBusy(true);
    setError('');
    setNote('');
    setPressOne(on);
    try {
      const r = await api.setInventoryPressOne(d, on);
      setNote(r.note ?? '');
      onSaved();
    } catch (err) {
      setPressOne(inv.pressOne);
      setError(err instanceof Error ? err.message : 'That didn’t go through. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card title="Open inventory" id="open-inventory" action={waiting ? <button type="button" className="link-btn" onClick={onShow}>{`Show ${waiting === 1 ? 'it' : `all ${waiting}`}`}</button> : <span className="muted">Empty</span>}>
      <div className="card-body inventory-card">
        <p className="muted">
          Numbers nobody uses yet wait here. Callers hear “Thank you for calling {company}. This number isn’t in use yet.”{pressOne ? ' “To reach our main menu, press 1.”' : ''} New numbers can wait here
          too, and any number can come back when it’s not needed any more (Change, on its row).
        </p>
        <label className="check">
          <input type="checkbox" checked={pressOne} disabled={!canChange || busy} onChange={(e) => void set(e.target.checked)} />
          <span>
            Offer your main menu: press 1
            <small className="muted"> Your main menu is the auto attendant most of your numbers go to.</small>
          </span>
        </label>
        {note && <Notice>{note}</Notice>}
        {error && <span className="field-error">{error}</span>}
      </div>
    </Card>
  );
}

const OPEN: NumberOrder['status'][] = ['working', 'needs_staff', 'porting'];

/** New numbers and moves: open ones first, then the last month's. */
function Orders({ d, orders, reload }: { d: string; orders: NumberOrder[]; reload: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState<NumberOrder | null>(null);
  const monthAgo = Date.now() - 30 * 86_400_000;
  const shown = orders.filter((o) => OPEN.includes(o.status) || new Date(o.updatedAt).getTime() > monthAgo).slice(0, 10);
  if (!shown.length) return null;
  const what = (o: NumberOrder) => (o.kind === 'port' ? `Moving ${plural(o.lines.length, 'number')} in` : `${plural(o.lines.length, 'new number')}`);
  return (
    <Card title="New numbers and moves">
      <ul className="order-list">
        {shown.map((o) => {
          const s = ORDER_STATUS[o.status];
          const expanded = open === o.id;
          return (
            <li key={o.id}>
              <button type="button" className="order-row" aria-expanded={expanded} onClick={() => setOpen(expanded ? null : o.id)}>
                <span className="cell-stack grow">
                  <strong>{what(o)}</strong>
                  <small className="muted">
                    {o.requestedByName}, {shortWhen(o.createdAt)}
                    {o.kind === 'port' && o.port && (o.port.focDate ? ` · moving on ${dateOnly(`${o.port.focDate}T12:00:00`)}` : ` · asked for ${dateOnly(`${o.port.wantedDate}T12:00:00`)}`)}
                  </small>
                </span>
                <Chip small tone={s.tone}>
                  {s.label}
                </Chip>
                <Icon name={expanded ? 'chev' : 'chevRight'} size={16} />
              </button>
              {expanded && (
                <div className="order-detail">
                  <OrderLines order={o} />
                  {o.kind === 'port' && o.status === 'porting' && (
                    <p className="muted small-print">
                      {o.port?.focDate
                        ? 'The date is confirmed. Keep your current service until then; Stitch support can still help if something changes.'
                        : 'Waiting for your current provider to confirm the date, usually a few days.'}{' '}
                      {!o.port?.focDate && (
                        <button type="button" className="link-btn danger-text" onClick={() => setCancelling(o)}>
                          Cancel the move
                        </button>
                      )}
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {error && (
        <div className="card-body">
          <Notice tone="crit">{error}</Notice>
        </div>
      )}
      {cancelling && (
        <Dialog
          open
          onClose={() => setCancelling(null)}
          title="Cancel this move?"
          foot={
            <>
              <Button
                variant="danger"
                onClick={async () => {
                  setError('');
                  try {
                    await api.cancelPort(d, cancelling.id);
                    reload();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                  setCancelling(null);
                }}
              >
                Cancel the move
              </Button>
              <Button variant="ghost" onClick={() => setCancelling(null)}>
                Keep it
              </Button>
            </>
          }
        >
          <p>The numbers stay with your current provider. You can ask to move them again later.</p>
        </Dialog>
      )}
    </Card>
  );
}
